import { ApolloError } from '@apollo/client';
import { DetailsRow, IColumn, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react';
import {
  Select,
  FluentProvider,
  webLightTheme,
  Tooltip,
  SpinButton,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import React, { useRef } from 'react';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import {
  addDurationToMilestones,
  updateMilestoneOffsetsCallback,
} from '../../../../utils/MilestoneHelpers';
import { IProcessTemplateVersionMilestone } from '../../../../utils/types/IProcessConfig';
import ShimmeredDetailsListWrapper from '../../../common/DetailsLists/ShimmeredDetailsListWrapper';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import SpanWithLabel from '../../../common/SpanWithLabel';
import { milestoneListColumns } from './CreateProcessConfigUtils';
import { MessagingColors } from '../../../../app/common/styles/CommonStyleObjects';

interface ICreateProcessConfigMilestonesFormProps {
  processTemplateMilestones: IProcessTemplateVersionMilestone[];
  onMilestonesChange: (newMilestone: IProcessTemplateVersionMilestone[]) => void;
  loading: boolean;
  error: ApolloError;
}

const useStyles = makeStyles({
  milestoneScrollContainer: {
    overflowY: 'auto',
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    '& .ms-DetailsList': {
      overflow: 'visible',
    },
    '& .ms-FocusZone': {
      overflow: 'visible',
    },
    '& .ms-DetailsList-headerWrapper': {
      position: 'sticky',
      top: '0',
      zIndex: 1,
      backgroundColor: tokens.colorNeutralBackground1,
    },
    '& .ms-DetailsList-headerWrapper .ms-DetailsHeader-cellName': {
      fontWeight: '600',
    },
  },
});

const CreateProcessConfigMilestonesForm = (
  props: ICreateProcessConfigMilestonesFormProps,
): JSX.Element => {
  const styles = useStyles();
  const { processTemplateMilestones, onMilestonesChange, error, loading } = props;

  const processTemplateMilestonesWithDurations = processTemplateMilestones
    ? addDurationToMilestones(processTemplateMilestones)
    : [];
  const milestonesRef = useRef(processTemplateMilestonesWithDurations);
  milestonesRef.current = processTemplateMilestonesWithDurations;
  const updateMilestoneConfigEdits = (
    newValue: IDropdownOption,
    milestoneConfigId: number,
  ): void => {
    const updatedProcessTemplateMilestones: IProcessTemplateVersionMilestone[] = [
      ...processTemplateMilestones,
    ];
    const editedMilestoneIndex = updatedProcessTemplateMilestones.findIndex(
      (u) => u.id === milestoneConfigId,
    );

    updatedProcessTemplateMilestones[editedMilestoneIndex] = {
      ...updatedProcessTemplateMilestones[editedMilestoneIndex],
      assigneeId: parseInt(newValue?.key?.toString(), 10),
    };
    onMilestonesChange(updatedProcessTemplateMilestones);
  };

  const renderItemColumn = (
    item: IProcessTemplateVersionMilestone,
    index: number,
    column: IColumn,
  ): JSX.Element => {
    const fieldContent = item[column.fieldName];

    switch (column.key) {
      case 'assigneeOptions': {
        if (fieldContent !== undefined) {
          const options = (fieldContent as { id: number; name: string }[]).map((option) => {
            return { key: option.id, text: option.name };
          });
          const assignedToGlobalUser = item.globalAssigneeId !== null;
          const selectedOption = assignedToGlobalUser
            ? options.find((o) => o.key === item.globalAssigneeId)
            : options.find((o) => o.key === item.assigneeId);

          return (
            <FluentProvider theme={webLightTheme}>
              <label htmlFor={`assignee-dropdown-${item.id}`}>{item.milestoneTemplateName}</label>
              <Select
                id={`assignee-dropdown-${item.id}`}
                placeholder="Please select assignee"
                defaultValue={selectedOption?.key?.toString() || ''}
                onChange={(event) => {
                  const selectedKey = event.target.value;
                  const selectedOpt = options.find((o) => o.key.toString() === selectedKey);
                  if (selectedOpt) {
                    updateMilestoneConfigEdits(selectedOpt, item.id);
                  }
                }}
                disabled={assignedToGlobalUser}
                aria-disabled={assignedToGlobalUser}
              >
                {options.map((option) => (
                  <option key={option.key.toString()} value={option.key.toString()}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </FluentProvider>
          );
        }
        return (
          <SpanWithLabel
            labelText={item.milestoneTemplateName}
            value={item?.assignee?.toString()}
          />
        );
      }
      case 'duration': {
        if (fieldContent !== 0) {
          return (
            <FluentProvider theme={webLightTheme}>
              <Tooltip
                withArrow
                content={<span>{`${item.minimumDuration} is the minimum`}</span>}
                relationship="label"
              >
                <SpinButton
                  aria-label="Edit duration"
                  value={fieldContent as number}
                  step={1}
                  onChange={(_event, data) => {
                    let newValue: number | undefined;
                    if (data.value !== undefined && data.value !== null) {
                      newValue = data.value;
                    } else if (data.displayValue) {
                      const parsed = parseFloat(data.displayValue);
                      if (Number.isFinite(parsed)) {
                        newValue = Math.round(parsed);
                      }
                    }
                    if (newValue === undefined) return;
                    const freshMilestones = milestonesRef.current.map((m) => ({
                      ...m,
                    }));
                    updateMilestoneOffsetsCallback(
                      newValue,
                      item.id,
                      freshMilestones,
                      (updatedMilestones) => {
                        milestonesRef.current = addDurationToMilestones(updatedMilestones);
                        onMilestonesChange(updatedMilestones as IProcessTemplateVersionMilestone[]);
                      },
                    );
                  }}
                />
              </Tooltip>
            </FluentProvider>
          );
        }
        return <>--</>;
      }
      case 'defaultDaysOffset': {
        if (fieldContent != null) {
          const defaultDaysOffset = Math.abs(parseInt(fieldContent as string, 10));
          return (
            <span>
              {defaultDaysOffset}
              &nbsp; days
            </span>
          );
        }
        return <>--</>;
      }
      default:
        return <span>{fieldContent}</span>;
    }
  };

  const onRenderRow = (rowProps: IDetailsRowProps) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (rowProps) {
      if (rowProps.item.maxDaysOffset === 0) {
        // Every other row renders with a different background color
        customStyles.root = { backgroundColor: MessagingColors.warning };
      }

      return <DetailsRow {...rowProps} styles={customStyles} />;
    }
    return null;
  };

  return (
    <div className={styles.milestoneScrollContainer}>
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="Loading the milestones for the process template"
      />
      <ShimmeredDetailsListWrapper
        columns={milestoneListColumns}
        items={processTemplateMilestonesWithDurations}
        onRenderRow={onRenderRow}
        enableShimmer={loading}
        onRenderItemColumn={renderItemColumn}
        singularListLabel="Milestone"
        pluralListLabel="Milestones"
      />
    </div>
  );
};
export default CreateProcessConfigMilestonesForm;
